import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../contexts/AuthContext';

const Posting = () => {
  const apiURL = process.env.REACT_APP_API_URL;
  const { user } = useAuth();
  const contentRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    category: 'ትምህርት',
    featured: false,
    image: ''
  });

  const [posts, setPosts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isRichTextMode, setIsRichTextMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadPosts = async () => {
    try {
      if (!apiURL) {
        setPosts([]);
        return;
      }

      const response = await fetch(`${apiURL}/news`);
      
      if (!response.ok) {
        throw new Error(`Failed to load posts: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle different response formats
      if (Array.isArray(data)) {
        setPosts(data);
      } else if (data.posts && Array.isArray(data.posts)) {
        setPosts(data.posts);
      } else if (data.data && Array.isArray(data.data)) {
        setPosts(data.data);
      } else {
        setPosts([]);
      }
    } catch (error) {
      // Silently fail — API not available locally
      console.warn('Posts load failed:', error.message);
      setPosts([]);
    }
  };

  // Load existing posts
  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Rich text formatting functions
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    contentRef.current.focus();
  };

  const handleRichTextChange = () => {
    if (contentRef.current) {
      setFormData(prev => ({
        ...prev,
        content: contentRef.current.innerHTML
      }));
    }
  };

  const insertQuote = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      
      if (selectedText) {
        const quoteElement = document.createElement('blockquote');
        quoteElement.className = 'quote-block';
        quoteElement.innerHTML = `"${selectedText}"`;
        
        range.deleteContents();
        range.insertNode(quoteElement);
        selection.removeAllRanges();
      } else {
        const quoteElement = document.createElement('blockquote');
        quoteElement.className = 'quote-block';
        quoteElement.innerHTML = '"ጥቅስዎን እዚህ ያስገቡ"';
        
        if (contentRef.current) {
          contentRef.current.appendChild(quoteElement);
        }
      }
      handleRichTextChange();
    }
  };

  const insertList = (type) => {
    formatText(type === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList');
  };

  // Image upload functionality
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('ፋይሉ ከ10MB በላይ ነው። እባክዎ ትንሽ ፋይል ይምረጡ።');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('የተደገፈ የፋይል አይነት አይደለም። JPEG, PNG, GIF ወይም WebP ይምረጡ።');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('image', file);
      
      console.log('Uploading file:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          console.log('Upload response status:', xhr.status);
          console.log('Upload response text:', xhr.responseText);
          
          if (xhr.status === 200) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (parseError) {
              console.error('JSON parse error:', parseError);
              reject(new Error('Invalid response format'));
            }
          } else {
            // Try to parse error response
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || 'Upload failed'));
            } catch (parseError) {
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
      });

      xhr.open('POST', `${apiURL}/upload`);
      xhr.send(formData);

      const result = await uploadPromise;

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          image: result.url
        }));
        toast.success('ምስል በተሳካ ሁኔታ ተጭኗል');
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('ምስል መጫን አልተሳካም: ' + error.message);
      toast.info('💡 ምክር: የምስል አድራሻ (URL) በመጠቀም ይሞክሩ', { autoClose: 5000 });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.info('ምስል ተወግዷል');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content || !formData.author) {
      toast.error('እባክዎ ሁሉንም አስፈላጊ መረጃዎች ያስገቡ');
      return;
    }

    try {
      if (editingId) {
        // Update existing post
        const response = await fetch(`${apiURL}/news/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) throw new Error('Update failed');

        const updatedPost = await response.json();
        setPosts(posts.map(post => post.id === editingId ? updatedPost : post));
        toast.success('ልጥፍ በተሳካ ሁኔታ ተሻሽሏል');
      } else {
        // Create new post
        const response = await fetch(`${apiURL}/news`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) throw new Error('Post creation failed');

        const newPost = await response.json();
        setPosts([newPost, ...posts]);
        toast.success('ልጥፍ በተሳካ ሁኔታ ታትሟል');
      }

      // Reset form
      setFormData({
        title: '',
        content: '',
        author: '',
        category: 'ትምህርት',
        featured: false,
        image: ''
      });
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
      }
      setEditingId(null);
    } catch (error) {
      toast.error('ልጥፍ በማስቀመጥ ላይ ስህተት ተፈጥሯል');
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      content: post.content,
      author: post.author,
      category: post.category,
      featured: post.featured,
      image: post.image || ''
    });
    
    if (contentRef.current && isRichTextMode) {
      contentRef.current.innerHTML = post.content;
    }
    
    setEditingId(post.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('እርግጠኛ ነዎት ይህን ልጥፍ መሰረዝ ይፈልጋሉ?')) {
      try {
        // First try the API delete
        if (apiURL) {
          const response = await fetch(`${apiURL}/news/${id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            // API delete successful
            setPosts(prevPosts => prevPosts.filter(post => post.id !== id));
            toast.success('ልጥፍ በተሳካ ሁኔታ ተሰርዟል');
            
            // If we were editing this post, clear the form
            if (editingId === id) {
              setEditingId(null);
              setFormData({
                title: '',
                content: '',
                author: '',
                category: 'ትምህርት',
                featured: false,
                image: ''
              });
              if (contentRef.current) {
                contentRef.current.innerHTML = '';
              }
            }
            return;
          } else {
            const errorText = await response.text();
            throw new Error(`API Delete failed: ${response.status} - ${errorText}`);
          }
        } else {
          throw new Error('No API URL configured');
        }
      } catch (error) {
        // Show specific error message
        toast.error(`የመሰረዝ ስህተት: ${error.message}`);
        
        // Fallback: Delete from local state only
        setPosts(prevPosts => prevPosts.filter(post => post.id !== id));
        toast.warning('ልጥፉ ከአካባቢያዊ ዝርዝር ተወግዷል። ገጹን ካደሱ በኋላ ሊመለስ ይችላል።');
        
        // If we were editing this post, clear the form
        if (editingId === id) {
          setEditingId(null);
          setFormData({
            title: '',
            content: '',
            author: '',
            category: 'ትምህርት',
            featured: false,
            image: ''
          });
          if (contentRef.current) {
            contentRef.current.innerHTML = '';
          }
        }
      }
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify({ posts }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'news.json';
    link.click();
    toast.success('ፋይል ወደ ኮምፒውተርዎ ወርዷል');
  };

  const toggleRichTextMode = () => {
    setIsRichTextMode(!isRichTextMode);
    if (!isRichTextMode && contentRef.current) {
      contentRef.current.innerHTML = formData.content.replace(/\n/g, '<br>');
    } else if (isRichTextMode && contentRef.current) {
      setFormData(prev => ({
        ...prev,
        content: contentRef.current.innerText
      }));
    }
  };

  const categories = ['ትምህርት', 'በዓላት'];

  return (
    <div className="page-container posting-theme">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Enhanced Responsive Page Header */}
      <div className="page-header">
        <h1 className="page-main-title">
          📝 ልጥፎችን ዓትም
        </h1>
        <div className="page-subtitle">
          Post Publishing - የአዲስ ዜና ወይም መረጃ ለማትም
        </div>
        <div className="page-description">
          <p className="page-description-main">
            የአዲስ ዜና፣ አስታወቂያ ወይም መረጃ ለማትም እና ለማስተዳደር
          </p>
          <p className="page-description-sub">
            Create and manage news, announcements, and information posts
          </p>
        </div>
      </div>

      <div className="posting-layout grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Enhanced Form Section */}
        <div className="content-container">
          <div className="content-header">
            <div className="content-icon">✍️</div>
            <h2 className="content-title">
              {editingId ? 'ልጥፍ አርትዕ' : 'አዲስ ልጥፍ ይፍጠሩ'}
            </h2>
          </div>
          
          <form onSubmit={handleSubmit} className="posting-form space-y-4 sm:space-y-6">
            <div className="form-group">
              <label htmlFor="title" className="form-label">
                📝 ርዕስ *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="ለምሳሌ: የገና በዓል አከባበር፣ የቅዱስ ሚካኤል በዓል..."
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <div className="content-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label htmlFor="content" className="form-label mb-0">
                  📄 ይዘት *
                </label>
                <button
                  type="button"
                  onClick={toggleRichTextMode}
                  className="rich-text-toggle"
                >
                  {isRichTextMode ? '📝 ቀላል ጽሑፍ' : '🎨 የተቀረጸ ጽሑፍ'}
                </button>
              </div>

              {isRichTextMode && (
                <div className="rich-text-toolbar">
                  <div className="toolbar-group">
                    <button type="button" onClick={() => formatText('bold')} className="toolbar-btn" title="ወፍራም">
                      <strong>B</strong>
                    </button>
                    <button type="button" onClick={() => formatText('italic')} className="toolbar-btn" title="ዘንበል">
                      <em>I</em>
                    </button>
                    <button type="button" onClick={() => formatText('underline')} className="toolbar-btn" title="ከስር መስመር">
                      <u>U</u>
                    </button>
                  </div>
                  
                  <div className="toolbar-group">
                    <button type="button" onClick={insertQuote} className="toolbar-btn" title="ጥቅስ">
                      💬
                    </button>
                    <button type="button" onClick={() => insertList('unordered')} className="toolbar-btn" title="ዝርዝር">
                      • 
                    </button>
                    <button type="button" onClick={() => insertList('ordered')} className="toolbar-btn" title="ቁጥር ዝርዝር">
                      1.
                    </button>
                  </div>

                  <div className="toolbar-group">
                    <select 
                      onChange={(e) => formatText('fontSize', e.target.value)} 
                      className="toolbar-select"
                      defaultValue="3"
                    >
                      <option value="1">ትንሽ</option>
                      <option value="3">መካከለኛ</option>
                      <option value="5">ትልቅ</option>
                      <option value="7">በጣም ትልቅ</option>
                    </select>
                  </div>

                  <div className="toolbar-group">
                    <button type="button" onClick={() => formatText('justifyLeft')} className="toolbar-btn" title="ወደ ግራ">
                      ⬅️
                    </button>
                    <button type="button" onClick={() => formatText('justifyCenter')} className="toolbar-btn" title="መሃል">
                      ↔️
                    </button>
                    <button type="button" onClick={() => formatText('justifyRight')} className="toolbar-btn" title="ወደ ቀኝ">
                      ➡️
                    </button>
                  </div>
                </div>
              )}

              {isRichTextMode ? (
                <div
                  ref={contentRef}
                  contentEditable
                  onInput={handleRichTextChange}
                  className="rich-text-editor"
                  placeholder="የበዓሉን ወይም የዜናውን ዝርዝር መረጃ ያስገቡ..."
                />
              ) : (
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="የበዓሉን ወይም የዜናውን ዝርዝር መረጃ ያስገቡ። ለምሳሌ: በዚህ ወር የሚከበረው የቅዱስ ሚካኤል በዓል ስለሆነ ሁሉም አማኞች ወደ ቤተክርስቲያን በመምጣት በጸሎት እንዲሳተፉ ይጋበዛሉ..."
                  rows="8"
                  className="form-textarea"
                  required
                />
              )}
            </div>

            <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="author" className="form-label">
                  👤 ደራሲ *
                </label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="ለምሳሌ: ቄስ አበበ፣ መምህር ተስፋዬ፣ ዲያቆን ዳንኤል..."
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category" className="form-label">
                  🏷️ ምድብ
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="image" className="form-label">
                🖼️ የምስል አድራሻ (URL) ወይም ፋይል ይጫኑ
              </label>
              
              {/* Image URL Input */}
              <input
                type="text"
                id="image"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="የቤተክርስቲያን ወይም የበዓል ምስል አድራሻ (አማራጭ)"
                className="form-input"
              />
              
              {/* File Upload Section */}
              <div className="image-upload-section mt-4">
                <div className="upload-options">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-file-input"
                  />
                  <label
                    htmlFor="image-file-input"
                    className={`upload-button ${isUploading ? 'uploading' : ''}`}
                  >
                    {isUploading ? (
                      <>
                        <span>📤</span>
                        እየተጫነ... ({uploadProgress}%)
                      </>
                    ) : (
                      <>
                        <span>📁</span>
                        ምስል ይምረጡ
                      </>
                    )}
                  </label>
                  
                  {formData.image && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="remove-image-btn"
                    >
                      <span>🗑️</span>
                      ምስል ሰርዝ
                    </button>
                  )}
                </div>
                
                {/* Upload Progress Bar */}
                {isUploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{uploadProgress}%</span>
                  </div>
                )}
                
                {/* Image Preview */}
                {formData.image && !isUploading && (
                  <div className="image-preview">
                    <div className="preview-container bg-gray-100 rounded-lg p-4 flex items-center justify-center" style={{ minHeight: '200px' }}>
                      <img 
                        src={formData.image} 
                        alt="Preview" 
                        className="preview-image max-w-full max-h-48 object-contain rounded-lg shadow-md"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          toast.error('ምስሉን መጫን አልተቻለም');
                        }}
                      />
                    </div>
                    <p className="image-url-display">
                      <strong>የምስል አድራሻ:</strong> 
                      <span className="url-text">{formData.image}</span>
                    </p>
                  </div>
                )}
                
                <p className="upload-note">
                  <strong>ማስታወሻ:</strong> የተደገፉ ፋይሎች: JPEG, PNG, GIF, WebP (ከ10MB በታች)
                  <br />
                  <strong>💡 ምክር:</strong> ፋይል መጫን ካልሰራ የምስል አድራሻ (URL) ይጠቀሙ
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="featured-checkbox">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="featured-input"
                />
                <span className="featured-label">⭐ እንደ ዋና ዜና አሳይ</span>
              </label>
            </div>

            <div className="form-actions flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button type="submit" className="submit-btn">
                {editingId ? (
                  <>
                    <span>🔄</span>
                    አዘምን
                  </>
                ) : (
                  <>
                    <span>📤</span>
                    አትም
                  </>
                )}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({
                      title: '',
                      content: '',
                      author: '',
                      category: 'ትምህርት',
                      featured: false,
                      image: ''
                    });
                    if (contentRef.current) {
                      contentRef.current.innerHTML = '';
                    }
                  }}
                >
                  ሰርዝ
                </button>
              )}
            </div>
          </form>

          <div className="export-section">
            <button onClick={handleExport} className="export-btn">
              <span>💾</span>
              news.json አውርድ (Backup)
            </button>
            <p className="export-note">
              ማስታወሻ: ልጥፎች በአገልጋይ ላይ በቋሚነት ተቀምጠዋል
            </p>
          </div>

          {/* Debug Section */}
          <div className="debug-section mt-4 p-4 bg-gray-100 rounded-lg">
            <h4 className="text-sm font-bold mb-2">🔧 Debug Information</h4>
            <div className="text-xs space-y-1">
              <p><strong>API URL:</strong> {apiURL || 'Not configured'}</p>
              <p><strong>Posts Count:</strong> {posts.length}</p>
              <p><strong>Currently Editing:</strong> {editingId || 'None'}</p>
              <p><strong>Cloudinary Status:</strong> Server-side configured (check upload test)</p>
            </div>
            <div className="mt-2 space-x-2">
              <button 
                onClick={() => {
                  toast.info(`Posts: ${posts.length}, API: ${apiURL ? 'Configured' : 'Not configured'}`);
                }}
                className="text-xs bg-blue-500 text-white px-2 py-1 rounded"
              >
                Log Debug Info
              </button>
              <button 
                onClick={loadPosts}
                className="text-xs bg-green-500 text-white px-2 py-1 rounded"
              >
                Reload Posts
              </button>
              <button 
                onClick={async () => {
                  try {
                    // Test without content-type header to let busboy handle it
                    const response = await fetch(`${apiURL}/upload`, {
                      method: 'POST'
                      // No headers - let the browser/busboy handle content type
                    });
                    const result = await response.json();
                    if (response.status === 400 && result.error === 'No image file provided') {
                      toast.success('✅ Upload endpoint is working! Cloudinary configured.');
                    } else {
                      toast.error(`Upload test failed: ${result.error}`);
                    }
                  } catch (error) {
                    toast.error(`Upload test error: ${error.message}`);
                  }
                }}
                className="text-xs bg-orange-500 text-white px-2 py-1 rounded"
              >
                Test Upload
              </button>
              <button 
                onClick={() => {
                  // Add a test post for debugging
                  const testPost = {
                    id: Date.now(),
                    title: 'Test Post for Delete',
                    content: 'This is a test post to verify delete functionality',
                    author: 'Test Author',
                    category: 'ትምህርት',
                    featured: false,
                    date: new Date().toLocaleDateString('am-ET')
                  };
                  setPosts(prev => [testPost, ...prev]);
                  toast.success('Test post added');
                }}
                className="text-xs bg-purple-500 text-white px-2 py-1 rounded"
              >
                Add Test Post
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Posts List Section */}
        <div className="posts-list-section bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200">
          <div className="posts-header mb-4 sm:mb-6">
            <h2 className="posts-section-title">
              <span className="text-2xl sm:text-3xl">📋</span>
              የታተሙ ልጥፎች ({posts.length})
            </h2>
          </div>
          <div className="posts-list space-y-4 max-h-96 overflow-y-auto">
            {posts.length === 0 ? (
              <div className="no-posts">
                <div className="no-posts-icon">
                  <span className="text-gray-400 text-2xl">📝</span>
                </div>
                <p className="no-posts-title">ገና ምንም ልጥፍ የለም</p>
                <p className="no-posts-subtitle">የመጀመሪያ ልጥፍዎን ይፍጠሩ</p>
              </div>
            ) : (
              posts.map(post => (
                <div key={post.id} className="post-item">
                  <div className="post-item-header">
                    <h3 className="post-item-title">{post.title}</h3>
                    {post.featured && (
                      <span className="featured-badge">
                        ⭐ ዋና
                      </span>
                    )}
                  </div>
                  <div 
                    className="post-item-content"
                    dangerouslySetInnerHTML={{
                      __html: post.content.length > 100 
                        ? post.content.substring(0, 100) + '...'
                        : post.content
                    }}
                  />
                  <div className="post-item-meta">
                    <span className="post-category">
                      {post.category}
                    </span>
                    <span className="post-author">👤 {post.author}</span>
                    <span className="post-date">📅 {post.date}</span>
                  </div>
                  <div className="post-item-actions">
                    {user && (
                      <button onClick={() => handleEdit(post)} className="btn-edit">
                        <span>✏️</span>
                        አርትዕ
                      </button>
                    )}
                    {user?.isAdmin && (
                      <button 
                        onClick={() => handleDelete(post.id)} 
                        className="btn-delete"
                      >
                        <span>🗑️</span>
                        ሰርዝ
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Posting;
